import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageLayout from '../components/PageLayout';
import { getArticle, mediaUrl } from '../api';
import { getFavorites, removeFavorite } from '../favorites';
import IconButton from '../components/ui/IconButton';
import PageHeader from '../components/ui/PageHeader';
import PageBody from '../components/ui/PageBody';
import PageMain from '../components/ui/PageMain';
import ArticleLink from '../components/ArticleLink';

function MultiSelectDropdown({
    label,
    options,
    selected,
    onChange,
    t
}) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(event) {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    function toggleOption(option) {
        if (selected.includes(option)) {
            onChange(selected.filter((item) => item !== option));
        } else {
            onChange([...selected, option]);
        }
    }

    return (
        <div
            className="account-choice"
            ref={dropdownRef}
            style={{ position: 'relative' }}
        >
            <button
                type="button"
                className="ui-button account-secondary-button"
                onClick={() => setIsOpen((value) => !value)}
                aria-expanded={isOpen}
            >
                <span>
                    {selected.length > 0
                        ? `${label} (${selected.length})`
                        : label}
                </span>

                <i
                    className={`fa-solid fa-chevron-${isOpen ? 'up' : 'down'}`}
                    aria-hidden="true"
                />
            </button>

            {isOpen && (
                <div
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 5px)',
                        left: 0,
                        zIndex: 10000,
                        width: '100%',
                        minWidth: '220px',
                        maxHeight: '260px',
                        overflowY: 'auto',
                        padding: '8px',
                        boxSizing: 'border-box',
                        background: 'var(--background-color, #fff)',
                        color: 'var(--text-color, #222)',
                        border: '1px solid var(--border-color, #ccc)',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    }}
                >
                    {options.length > 0 ? (
                        options.map((option) => (
                            <label
                                key={option}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    padding: '1px 5px',
                                    cursor: 'pointer',
                                    borderRadius: '5px',
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={selected.includes(option)}
                                    onChange={() => toggleOption(option)}
                                    style={{
                                        flexShrink: 0,
                                        width: '16px',
                                        height: '16px',
                                        margin: 0,
                                    }}
                                />

                                <span>{option}</span>
                            </label>
                        ))
                    ) : (
                        <p style={{ padding: '8px', margin: 0 }}>
                            {t('articlesPage.noOptions')}
                        </p>
                    )}

                    {selected.length > 0 && (
                        <button
                            type="button"
                            className="ui-button account-secondary-button"
                            onClick={() => onChange([])}
                            style={{
                                width: '100%',
                                marginTop: '6px',
                            }}
                        >
                            {t('articlesPage.clearSelection')}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

function Favorites() {
    const { t, i18n } = useTranslation();

    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);

    const [sortBy, setSortBy] = useState('default');
    const [sortDirection, setSortDirection] = useState('asc');

    const [selectedTags, setSelectedTags] = useState([]);
    const [selectedAuthors, setSelectedAuthors] = useState([]);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        let cancelled = false;

        async function loadFavorites() {
            try {
                const favorites = await getFavorites();

                const loadedArticles = await Promise.all(
                    favorites.map(async (favorite) => {
                        const article = await getArticle(favorite.article_id);

                        return {
                            ...favorite,
                            article,
                        };
                    })
                );

                if (!cancelled) {
                    setArticles(loadedArticles);
                }
            } catch (error) {
                console.error('Failed to load favorites:', error);
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        loadFavorites();

        return () => {
            cancelled = true;
        };
    }, []);

    // creation_date хранится в Unix timestamp (секунды)
    const getArticleTimestamp = (item) => {
        const timestamp = Number(item.article?.creation_date);

        return Number.isFinite(timestamp) ? timestamp : 0;
    };

    const authors = useMemo(() => {
        return [
            ...new Set(
                articles
                    .map(
                        (item) =>
                            item.article?.author?.name ||
                            item.article?.author?.nickname
                    )
                    .filter(Boolean)
            ),
        ].sort((a, b) => a.localeCompare(b, i18n.language));
    }, [articles, i18n.language]);

    const tags = useMemo(() => {
        return [
            ...new Set(
                articles
                    .flatMap((item) =>
                        (item.article?.tags || []).map((tag) =>
                            typeof tag === 'string' ? tag : tag?.name
                        )
                    )
                    .filter(Boolean)
            ),
        ].sort((a, b) => a.localeCompare(b, i18n.language));
    }, [articles, i18n.language]);

    const filteredArticles = useMemo(() => {
        return articles.filter((item) => {
            const article = item.article;

            if (!article) return false;

            const articleTags = (article.tags || [])
                .map((tag) =>
                    typeof tag === 'string' ? tag : tag?.name
                )
                .filter(Boolean);

            if (
                selectedTags.length > 0 &&
                !selectedTags.some((tag) => articleTags.includes(tag))
            ) {
                return false;
            }

            const author =
                article.author?.name ||
                article.author?.nickname ||
                '';

            if (
                selectedAuthors.length > 0 &&
                !selectedAuthors.includes(author)
            ) {
                return false;
            }

            const timestamp = getArticleTimestamp(item);

            if (!timestamp && (dateFrom || dateTo)) {
                return false;
            }

            const articleDate = timestamp
                ? new Date(timestamp * 1000)
                : null;

            if (dateFrom) {
                const from = new Date(`${dateFrom}T00:00:00`);

                if (!articleDate || articleDate < from) {
                    return false;
                }
            }

            if (dateTo) {
                const to = new Date(`${dateTo}T23:59:59.999`);

                if (!articleDate || articleDate > to) {
                    return false;
                }
            }

            return true;
        });
    }, [
        articles,
        selectedTags,
        selectedAuthors,
        dateFrom,
        dateTo,
    ]);

    const displayedArticles = useMemo(() => {
        const result = [...filteredArticles];

        if (sortBy === 'title') {
            result.sort((a, b) =>
                (a.article?.title || '').localeCompare(
                    b.article?.title || '',
                    i18n.language,
                    {
                        numeric: true,
                        sensitivity: 'base',
                    }
                )
            );
        } else if (sortBy === 'author') {
            result.sort((a, b) => {
                const authorA =
                    a.article?.author?.name ||
                    a.article?.author?.nickname ||
                    '';

                const authorB =
                    b.article?.author?.name ||
                    b.article?.author?.nickname ||
                    '';

                return authorA.localeCompare(
                    authorB,
                    i18n.language
                );
            });
        } else if (sortBy === 'date') {
            result.sort((a, b) =>
                getArticleTimestamp(a) - getArticleTimestamp(b)
            );
        }

        if (sortBy !== 'default' && sortDirection === 'desc') {
            result.reverse();
        }

        return result;
    }, [
        filteredArticles,
        sortBy,
        sortDirection,
        i18n.language,
    ]);

    function resetFilters() {
        setSortBy('default');
        setSortDirection('asc');
        setSelectedTags([]);
        setSelectedAuthors([]);
        setDateFrom('');
        setDateTo('');
    }

    async function handleRemove(articleId) {
        try {
            await removeFavorite(articleId);

            setArticles((previous) =>
                previous.filter(
                    (item) => item.article_id !== articleId
                )
            );
        } catch (error) {
            console.error('Failed to remove favorite:', error);
        }
    }

    function openDatePicker(id) {
        const input = document.getElementById(id);

        if (input?.showPicker) {
            input.showPicker();
        } else {
            input?.focus();
        }
    }

    return (
        <PageLayout showSearch showAiSearch>
            <PageMain className="articles-page-main">
                <PageHeader
                    title={t('common.myFavorites')}
                    condensed
                    note={
                        !loading && articles.length === 0
                            ? t('common.emptyNote')
                            : null
                    }
                />

                {!loading && articles.length > 0 && (
                    <div className="article-controls article-controls--articles-page">
                        <div className="article-controls__top">
                            <div className="account-choice article-sort-wrapper">
                                <button
                                    type="button"
                                    className="ui-button account-secondary-button"
                                    onClick={() =>
                                        document
                                            .getElementById('favorites-sort-select')
                                            .click()
                                    }
                                >
                                    <span>
                                        {sortBy === 'default'
                                            ? t('articlesPage.sortDefault')
                                            : sortBy === 'title'
                                                ? t('articlesPage.sortTitle')
                                                : sortBy === 'author'
                                                    ? t('articlesPage.filterByAuthor')
                                                    : t('articlesPage.sortDate')}
                                    </span>

                                    <i
                                        className="fa-solid fa-chevron-down"
                                        aria-hidden="true"
                                    />
                                </button>

                                <select
                                    id="favorites-sort-select"
                                    value={sortBy}
                                    onChange={(event) =>
                                        setSortBy(event.target.value)
                                    }
                                    aria-label={t('articlesPage.sortLabel')}
                                >
                                    <option value="default">
                                        {t('articlesPage.sortDefault')}
                                    </option>
                                    <option value="title">
                                        {t('articlesPage.sortTitle')}
                                    </option>
                                    <option value="date">
                                        {t('articlesPage.sortDate')}
                                    </option>
                                </select>
                            </div>

                            <button
                                type="button"
                                className="ui-button account-secondary-button"
                                onClick={() =>
                                    setSortDirection((direction) =>
                                        direction === 'asc' ? 'desc' : 'asc'
                                    )
                                }
                                aria-label={t(
                                    sortDirection === 'asc'
                                        ? 'articlesPage.sortAscending'
                                        : 'articlesPage.sortDescending'
                                )}
                                disabled={sortBy === 'default'}
                            >
                                {sortDirection === 'asc' ? '↑' : '↓'}
                            </button>

                            <button
                                type="button"
                                className="ui-button account-secondary-button"
                                onClick={() =>
                                    setShowFilters((value) => !value)
                                }
                                aria-expanded={showFilters}
                            >
                                <i
                                    className="fa-solid fa-filter"
                                    aria-hidden="true"
                                />

                                {showFilters
                                    ? t('articlesPage.hideFilters')
                                    : t('articlesPage.showFilters')}
                            </button>

                            <button
                                type="button"
                                className="ui-button account-secondary-button"
                                onClick={resetFilters}
                            >
                                <i
                                    className="fa-solid fa-rotate-left"
                                    aria-hidden="true"
                                />

                                {t('articlesPage.resetFilters')}
                            </button>
                        </div>

                        {showFilters && (
                            <div
                                className="article-controls__filters"
                                style={{ overflow: 'visible' }}
                            >
                                <MultiSelectDropdown
                                    label={t('articlesPage.filterByTag')}
                                    options={tags}
                                    selected={selectedTags}
                                    onChange={setSelectedTags}
                                    t={t}
                                />

                                <MultiSelectDropdown
                                    label={t('articlesPage.filterByAuthor')}
                                    options={authors}
                                    selected={selectedAuthors}
                                    onChange={setSelectedAuthors}
                                    t={t}
                                />

                                <div className="ui-button account-secondary-button">
                                    <label htmlFor="favorites-date-from">
                                        {t('articlesPage.dateFrom')}
                                    </label>

                                    <div className="article-date-filter__control">
                                        <input
                                            id="favorites-date-from"
                                            type="date"
                                            value={dateFrom}
                                            onChange={(event) =>
                                                setDateFrom(event.target.value)
                                            }
                                            className="article-date-filter__input"
                                        />

                                        <button
                                            type="button"
                                            className="article-date-filter__button"
                                            onClick={() =>
                                                openDatePicker('favorites-date-from')
                                            }
                                            aria-label={t('articlesPage.dateFrom')}
                                        >
                                            <i
                                                className="fa-regular fa-calendar"
                                                aria-hidden="true"
                                            />
                                        </button>
                                    </div>
                                </div>

                                <div className="ui-button account-secondary-button">
                                    <label htmlFor="favorites-date-to">
                                        {t('articlesPage.dateTo')}
                                    </label>

                                    <div className="article-date-filter__control">
                                        <input
                                            id="favorites-date-to"
                                            type="date"
                                            value={dateTo}
                                            min={dateFrom || undefined}
                                            onChange={(event) =>
                                                setDateTo(event.target.value)
                                            }
                                            className="article-date-filter__input"
                                        />

                                        <button
                                            type="button"
                                            className="article-date-filter__button"
                                            onClick={() =>
                                                openDatePicker('favorites-date-to')
                                            }
                                            aria-label={t('articlesPage.dateTo')}
                                        >
                                            <i
                                                className="fa-regular fa-calendar"
                                                aria-hidden="true"
                                            />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {loading ? (
                    <PageBody>
                        <p className="page-message">
                            {t('common.loading')}
                        </p>
                    </PageBody>
                ) : articles.length === 0 ? (
                    <PageBody>
                        <p className="page-message">
                            {t('common.emptyNote')}
                        </p>
                    </PageBody>
                ) : displayedArticles.length === 0 ? (
                    <PageBody>
                        <p className="page-message">
                            {t('articlesPage.noMatchingArticles')}
                        </p>
                    </PageBody>
                ) : (
                    <PageBody className="articles-list">
                        {displayedArticles.map((item) => (
                            <article
                                key={item.article_id}
                                className="article-list-item"
                            >
                                <div className="favorite-card-content">
                                    <ArticleLink
                                        className="article-list-title"
                                        article={{
                                            ...item.article,
                                            article_id: item.article_id,
                                        }}
                                    >
                                        {item.article?.icon ? (
                                            <img
                                                className="article-list-title-image"
                                                src={mediaUrl(item.article.icon)}
                                                alt=""
                                            />
                                        ) : (
                                            <span
                                                className="article-list-document-icon"
                                                aria-hidden="true"
                                            >
                                                <i className="fa-regular fa-image" />
                                                <span className="article-list-image-crack" />
                                            </span>
                                        )}

                                        <span>{item.article.title}</span>
                                    </ArticleLink>
                                </div>

                                <div className="article-list-actions">
                                    <IconButton
                                        className="article-visibility-button"
                                        icon="fa-solid fa-star"
                                        label={t('favorites.remove')}
                                        onClick={() =>
                                            handleRemove(item.article_id)
                                        }
                                    />
                                </div>
                            </article>
                        ))}
                    </PageBody>
                )}
            </PageMain>
        </PageLayout>
    );
}

export default Favorites;